import React, { useState } from 'react';
import { Form, Formik } from 'formik';

import Button, { ButtonStyleType } from '@/components/common/Button/Button';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import { Fields, validationSchema } from './validation';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';

import { ContractCommonData } from '@/graphql/contracts/_generated_/ContractCommonData.fragment';
import { UpdateContractCustomerInputType } from '@/graphql/contracts/_generated_/contracts';

import useSearchCustomers from '@/pages/AddContract/hooks/useSearchCustomers';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import { Customer } from '@/pages/AddContract/AddContract.container';


interface IFormProps {
  onSubmit: (newContractCustomer: UpdateContractCustomerInputType) => Promise<void>
  onClose: () => void
  contract: ContractCommonData;
}

export default function UpdateCustomerModal({
  onSubmit,
  onClose,
  contract,
}: IFormProps) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { isSearchLoading, searchCustomers, searchCustomersData } = useSearchCustomers();

  const handleSubmit = async (newCustomerId: string) => {
    await onSubmit({
      currentCustomerId: contract.customer.customerID,
      contractId: contract.contractID,
      newCustomerId: newCustomerId
    });

    setShowConfirmationModal(false);
  };

  return (
    <>
      <AgModalWindow
        isOpen={true}
        onClose={onClose}
        title="Update Account"
        contentStyle={{ width: 340 }}
      >
        <Formik<Fields>
          initialValues={{
            customerID: contract.customer.customerID,
          }}
          validationSchema={validationSchema}
          onSubmit={() => setShowConfirmationModal(true)}
          validateOnMount={true}
        >
          {({ values, errors, touched, isValid, dirty, setFieldValue, setFieldTouched }) => (
            <Form>
              <AgConfirmationModal
                isOpen={showConfirmationModal}
                title="You are about to change the Account for this Contract"
                onClose={() => setShowConfirmationModal(false)}
                onConfirm={async () => { await handleSubmit(values.customerID); }}
                contentStyle={{ width: 650 }}
              >
              Please, make sure that Bill to Parent, Consolidate settings are valid for the updated Customer. <br />
              Do you want to proceed?
              </AgConfirmationModal>
              <div className="mb-15">
                <ModalFormDropdown<Customer>
                  id="customerId"
                  labelTitle="Account"
                  optionsData={searchCustomersData || []}
                  getOptionLabel={(option) => option.customerName}
                  getOptionValue={(option) => option.customerID}
                  defaultValue={{
                    ...contract.customer
                  }}
                  menuPortalTarget={document.querySelector('body')}
                  isFloatingValidation={true}
                  isSearchable={true}
                  handleChange={(option) => {
                    setFieldTouched('customerID');
                    setFieldValue('customerID', option?.customerID ?? '');
                  }}
                  handleInputChange={(value) => searchCustomers(value)}
                  isLoading={isSearchLoading}
                  validationMessage={
                    (errors?.customerID && touched.customerID) ? errors.customerID : undefined
                  }
                />
              </div>
              <ActionButtonsWrapper
                leftButton={(
                  <Button
                    buttonStyleType={ButtonStyleType.SECONDARY}
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                )}
                rightButton={(
                  <Button
                    disabled={!isValid || !dirty}
                    buttonStyleType={ButtonStyleType.PRIMARY}
                    type="submit"
                  >
                    Save
                  </Button>
                )}
              />
            </Form>
          )}
        </Formik>
      </AgModalWindow>
    </>
  );
}